import { Component, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { BankStore, ContactStore, InsuranceStore } from '../../core/stores/base.store';
import { Contact } from '../../core/models';
import { parseYYYYMMDDToDate } from '../../core/utils/date.utils';
import { FuseConfirmationService } from '../../core/services/confirmation/confirmation.service';
import { NotificationUtils } from '../../core/utils';
import { BankDepositDialogComponent } from '../bank-deposit-dialog/bank-deposit-dialog.component';
import { BankWithdrawDialogComponent } from '../bank-withdraw-dialog/bank-withdraw-dialog.component';
@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    BankDepositDialogComponent,
    BankWithdrawDialogComponent,
  ],
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.css']
})
export class RegisterComponent {
  bankStore = inject(BankStore);
  contactStore = inject(ContactStore);
  insuranceStore = inject(InsuranceStore);
  dialog = inject(MatDialog);
  private _fuseConfirmationService = inject(FuseConfirmationService);

  contactTransactions = computed(() => {
    const selectedBank = this.selectedBank();
    if (!selectedBank) return [];
    
    const transactions = selectedBank.bank_transactions_collection || [];
    const startingBalance = selectedBank.starting_balance || 0;
        
    // Process transactions while preserving original date strings
    const processedTransactions = [...transactions].map(transaction => {
      // Keep the original transaction_date string for display
      const originalDateString = transaction.transaction_date;
      
      // For sorting purposes, parse the date string
      let transactionDate = null;
      let formattedDateString = '';
      
      if (originalDateString) {
        try {
          // Parse the date string to a Date object for sorting
          transactionDate = parseYYYYMMDDToDate(originalDateString);
          
          // Format the date for display directly from the original string components
          // This ensures we don't have timezone issues when formatting
          const [year, month, day] = originalDateString.split('-').map(Number);
          formattedDateString = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
        } catch (e) {
          console.error('Invalid date:', originalDateString, e);
          transactionDate = new Date(); // Fallback to current date
          formattedDateString = originalDateString; // Use original string
        }
      } else {
        transactionDate = new Date(); // Default to current date if null
        formattedDateString = ''; // Empty string for display
      }
      
      return {
        ...transaction,
        transaction_date: transactionDate,
        original_date_string: formattedDateString // Use formatted date for display
      };
    });
    
    // Sort by date
    const sortedTransactions = processedTransactions.sort((a, b) => 
      a.transaction_date.getTime() - b.transaction_date.getTime()
    );
        
    // Find the latest payment for each loan
    const latestPaymentByLoan = new Map<number, number>(); // Map of loan_id to transaction index
    
    // Go through transactions in reverse (newest first) to find latest payment for each loan
    for (let i = sortedTransactions.length - 1; i >= 0; i--) {
      const transaction = sortedTransactions[i];
      if (transaction.loan_id && !latestPaymentByLoan.has(transaction.loan_id)) {
        latestPaymentByLoan.set(transaction.loan_id, i);
      }
    }

    // Calculate running balance for each transaction
    let runningBalance = Number(startingBalance);
    
    const transactionsWithBalance = sortedTransactions.map((transaction, index) => {
      // Calculate payment and deposit based on amount
      let payment = 0;
      let deposit = 0;
      
      if (transaction.amount < 0) {
        payment = Math.abs(transaction.amount);
        runningBalance -= Number(payment);
      } else {
        deposit = transaction.amount;
        runningBalance += Number(deposit);
      }
      
      // Check if this is the latest payment for its loan
      const isLatestPayment = transaction.loan_id && 
        latestPaymentByLoan.has(transaction.loan_id) && 
        latestPaymentByLoan.get(transaction.loan_id) === index;
      
      // Add additional properties to the transaction
      return {
        ...transaction,
        index,
        payment,
        deposit,
        balance: runningBalance,
        reconciled: false, // Default value for reconciled
        isLatestPayment,
        isStartingBalance: false // Not a starting balance entry
      };
    });
    
    // Add starting balance as the first entry if there are transactions
    if (startingBalance !== null && startingBalance !== undefined && transactionsWithBalance.length > 0) {
      const firstTransactionDate = transactionsWithBalance[0].transaction_date;
      
      // Create a date string for the day before the first transaction
      let dayBeforeString = '';
      let dayBefore = null;
      
      if (firstTransactionDate) {
        // Create a new date object for the day before
        dayBefore = new Date(firstTransactionDate);
        dayBefore.setDate(dayBefore.getDate() - 1);
        
        // Format the date directly using the date components to avoid timezone issues
        const year = dayBefore.getFullYear();
        const month = dayBefore.getMonth() + 1; // Convert from 0-11 to 1-12
        const day = dayBefore.getDate();
        
        // Create YYYY-MM-DD format for internal use
        const internalDateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        
        // Create MM/DD/YYYY format for display
        dayBeforeString = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
        
        // Re-parse the internal date string to ensure consistency
        dayBefore = parseYYYYMMDDToDate(internalDateString);
      } else {
        // Fallback if no first transaction date
        dayBefore = new Date();
        const year = dayBefore.getFullYear();
        const month = dayBefore.getMonth() + 1;
        const day = dayBefore.getDate();
        dayBeforeString = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
      }
      
      const startingBalanceEntry = {
        id: -1, // Special ID for starting balance
        bank_id: selectedBank.id,
        contact_id: 0,
        loan_id: 0,
        insurance_id: 0,
        insurance_loan_id: 0,
        category: 'starting_balance',
        amount: startingBalance,
        principal: 0,
        interest: 0,
        late_fee: null,
        transaction_date: dayBefore,
        original_date_string: dayBeforeString,
        loan_amortization_number: null,
        index: -1,
        payment: 0,
        deposit: startingBalance,
        balance: startingBalance,
        reconciled: true,
        isLatestPayment: false,
        isStartingBalance: true
      };
      
      // Add to the beginning of the array
      transactionsWithBalance.unshift(startingBalanceEntry);
    }
    
    return transactionsWithBalance;
  });

  selectedBank = computed(() => this.bankStore.selected());
  loading = computed(() => this.bankStore.loading());
  hasLoans = computed(() => this.selectedBank()?.loans_collection?.length > 0);
  hasInsurance = computed(() => this.insuranceStore.entities().length > 0);

  /**
   * Opens the deposit dialog
   */
  navigateToDeposit(): void {
    const dialogRef = this.dialog.open(BankDepositDialogComponent, {
      width: '500px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Refresh data if needed
      }
    });
  }

  formatString(str: string) {
    return NotificationUtils.getInstance().formatEntityName(str);
  }

  /**
   * Opens the withdraw dialog
   */
  navigateToWithdrawForm(): void {
    const dialogRef = this.dialog.open(BankWithdrawDialogComponent, {
      width: '500px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Refresh data if needed
      }
    });
  }

  /**
   * Gets the name of a contact by ID
   * @param contactId The ID of the contact
   * @param isStartingBalance Whether this is a starting balance entry
   * @param transaction The transaction object
   * @returns The name of the contact
   */
  get_name(contactId: number, isStartingBalance = false, transaction: any = null): string {
    if (isStartingBalance) return 'Starting Balance';
    if (!contactId) return '';
    
    const contact = this.contactStore.getEntity(contactId) as Contact;
    if (!contact) return `Contact #${contactId}`;
    
    if (contact.category == 'Insurance')
      return `${contact.organization} - ${contact.first_name} ${contact.last_name}`
    
    // Check if the contact has first_name and last_name
    if (contact.first_name || contact.last_name) {
      return `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
    }
    
    // If no name is available, use organization name if present
    if (contact.organization) {
      return contact.organization;
    }
    
    // Last resort fallback
    return `Contact #${contactId}`;
  }

  /**
   * Gets the name of a loan by ID
   * @param loanId The ID of the loan
   * @param isStartingBalance Whether this is a starting balance entry
   * @param transaction The transaction object
   * @returns The name of the loan
   */
  get_loan_name(loanId: number, isStartingBalance = false, transaction: any = null): string {
    if (isStartingBalance) return '';
    if (!loanId) return '';
    
    const bank = this.selectedBank();
    if (!bank || !bank.loans_collection) return `Loan #${loanId}`;
    
    const loan = bank.loans_collection.find(l => l.id === loanId);
    if (!loan) return `Loan #${loanId}`;
    
    return loan.name || `Loan #${loanId}`;
  }

  /**
   * Delete a transaction after confirmation
   */
  deleteTransaction(transaction: any): void {
    // Don't allow deleting the starting balance entry
    if (transaction.isStartingBalance) return;

    const confirmation = this._fuseConfirmationService.open({
      title: 'Delete Transaction',
      message: 'Are you sure you want to delete this transaction? This action cannot be undone.',
      actions: {
        confirm: {
          label: 'Delete',
          color: 'warn'
        }
      }
    });

    // Subscribe to the confirmation dialog closed action
    confirmation.afterClosed().subscribe((result) => {
      // Handle both formats: string 'confirmed' (legacy) or object with result property (new)
      const isConfirmed = result === 'confirmed' || 
                         (result && typeof result === 'object' && result.result === 'confirmed');
                         
      if (isConfirmed) {
        const selectedBank = this.bankStore.selected();
        if (!selectedBank || !transaction.id) return;
        this.bankStore.deleteChild({
          child: transaction, 
          endpoint: 'bank_transaction', 
          collectionName: 'bank_transactions_collection'
        });
      }
    });
  }

  /**
   * Checks if a transaction has loan-related details to display in a tooltip
   * @param transaction The transaction to check
   * @returns True if the transaction has loan details
   */
  hasLoanDetails(transaction: any): boolean {
    if (!transaction) return false;
    
    // Check if this is a loan payment (has loan_id or any loan-related fields)
    return (
      transaction.loan_id || 
      (transaction.principal !== null && transaction.principal !== undefined) ||
      (transaction.interest !== null && transaction.interest !== undefined) ||
      (transaction.late_fee !== null && transaction.late_fee !== undefined) ||
      (transaction.insurance_loan_id !== null && transaction.insurance_loan_id !== undefined)
    );
  }

  /**
   * Generates tooltip content for loan payment details
   * @param transaction The transaction to generate tooltip for
   * @returns Formatted tooltip text with loan details
   */
  getLoanPaymentDetails(transaction: any): string {
    if (!this.isLoanPayment(transaction)) return '';
    
    const details: string[] = [];
    
    // Add loan name if available (first line)
    if (transaction.loan_id) {
      const loanName = this.get_loan_name(transaction.loan_id, false, transaction);
      details.push(`Loan: ${loanName}`);
    } else if (transaction.insurance_loan_id) {
      const insuranceLoan = this.getInsuranceLoanName(transaction.insurance_loan_id);
      details.push(`Insurance Loan: ${insuranceLoan}`);
    } else {
      details.push('Loan Payment');
    }
    
    // Add principal amount if available (second line)
    if (transaction.principal !== null && transaction.principal !== undefined) {
      const principal = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(transaction.principal);
      details.push(`Principal: ${principal}`);
    } else {
      details.push('Principal: $0.00');
    }
    
    // Add interest amount if available (third line)
    if (transaction.interest !== null && transaction.interest !== undefined) {
      const interest = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(transaction.interest);
      details.push(`Interest: ${interest}`);
    } else {
      details.push('Interest: $0.00');
    }
    
    // Add late fee if available (fourth line)
    if (transaction.late_fee !== null && transaction.late_fee !== undefined && +transaction.late_fee > 0) {
      const lateFee = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(transaction.late_fee);
      details.push(`Late Fee: ${lateFee}`);
    } else {
      details.push('Late Fee: $0.00');
    }
    
    return details.join('\n');
  }

  /**
   * Gets the name of an insurance loan by ID
   * @param insuranceLoanId The ID of the insurance loan
   * @returns The name of the insurance loan
   */
  getInsuranceLoanName(insuranceLoanId: number): string {
    if (!insuranceLoanId) return '';
    
    const insurance = this.insuranceStore.selected();
    if (!insurance || !insurance.insurance_loans_collection) return `Insurance Loan #${insuranceLoanId}`;
    
    const insuranceLoan = insurance.insurance_loans_collection.find(l => l.id === insuranceLoanId);
    if (!insuranceLoan) return `Insurance Loan #${insuranceLoanId}`;
    
    return insuranceLoan.name || `Insurance Loan #${insuranceLoanId}`;
  }

  /**
   * Checks if a transaction is a loan payment
   * @param transaction The transaction to check
   * @returns True if the transaction is a loan payment
   */
  isLoanPayment(transaction: any): boolean {
    if (!transaction) return false;
    
    // Check if this transaction has category "Loan payment"
    return transaction.category === 'Loan payment';
  }
}
